import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

import { ConfigProvider, Typography } from 'antd';

import messages from '../../constants/messages';

import {
  signIn as doSignIn,
  signUp as doSignUp,
  resendEmailCode,
  savePhone,
  verifyEmail as verifyEmailOtp,
  verifyPhone as verifyPhoneOtp
} from '../../redux/actions/auth';

import { errorToastHandler, infoToastHandler } from '../../components/action_notifier';

import background from '../../assets/images/diagri-background.png';
import AuthAside from './AuthAside';
import AuthFooter from './AuthFooter';
import SignInForm from './SignInForm';
import SignUpForm from './SignUpForm';
import { STEPS } from './constants';

const { Title } = Typography;

const sidebarBackground = {
  backgroundImage: `url(${background})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center'
};

function Auth({ baseURL }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const path = useLocation();

  const signInFormRef = useRef(null);
  const signUpFormRef = useRef(null);

  const { loading } = useSelector(state => state.auth);

  const [step, setStep] = useState(STEPS.EMAIL);
  const [agreementFieldVisibility, setAgreementFieldVisibility] = useState(false);
  const [isSignUpMode, setIsSignUpMode] = useState(path?.pathname === '/auth/register');
  const [state, setState] = useState({ logging: false });

  useEffect(() => {
    const isRegister = path?.pathname === '/auth/register';
    setIsSignUpMode(isRegister);
  }, [path?.pathname]);

  const signIn = async () => {
    if (state.logging) return;

    const values = await signInFormRef.current?.validateFields();
    const { email, password, isTermsAccepted } = values;

    if (!email || !password) return;

    try {
      setState(prev => ({ ...prev, logging: true }));
      await dispatch(
        doSignIn({
          email,
          password,
          pushPlatform: 'web',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          isTermsAccepted
        })
      ).unwrap();

      setState(prev => ({ ...prev, logging: false }));

      history.push(baseURL ?? '/');
    } catch (error) {
      if (error?.field === 'email') {
        setStep(STEPS.EMAIL_OTP);
      }

      if (error?.field === 'phone') {
        setStep(STEPS.PHONE);
      }

      if (error?.field === 'agreement') {
        setAgreementFieldVisibility(true);
      }

      setState(prev => ({ ...prev, logging: false }));
    }
  };

  const handleNextStep = useCallback(async () => {
    try {
      if (step === STEPS.EMAIL) {
        const values = await signUpFormRef.current?.validateFields();
        await dispatch(doSignUp(values)).unwrap();
        setStep(STEPS.EMAIL_OTP);
      } else if (step === STEPS.EMAIL_OTP) {
        const { email, emailOtp } = await signUpFormRef.current?.validateFields();
        await dispatch(verifyEmailOtp({ email, code: emailOtp })).unwrap();
        setStep(STEPS.PHONE);
      } else if (step === STEPS.PHONE) {
        const { email, phone } = await signUpFormRef.current?.validateFields();
        await dispatch(savePhone({ email, phone: `+${phone}` })).unwrap();
        setStep(STEPS.PHONE_OTP);
      } else if (step === STEPS.PHONE_OTP) {
        const { email, phone, phoneOtp } = await signUpFormRef.current?.validateFields();
        await dispatch(verifyPhoneOtp({ email, phone: `+${phone}`, code: phoneOtp })).unwrap();
        setStep(STEPS.DONE);
        history.push('/profile');
      }
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }, [step]);

  const switchToSignIn = useCallback(async () => {
    setIsSignUpMode(false);
    history.replace('/auth/login');
  }, []);

  async function sendPhone() {
    try {
      const { email, phone } = await signInFormRef.current?.validateFields();
      await dispatch(savePhone({ email, phone: `+${phone}` })).unwrap();
      setStep(STEPS.PHONE_OTP);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  async function verifyEmail() {
    try {
      const { email, emailOtp } = await signInFormRef.current?.validateFields();
      await dispatch(verifyEmailOtp({ email, code: emailOtp })).unwrap();
      await signIn();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  async function verifyPhone() {
    try {
      const { email, phone, phoneOtp } = await signInFormRef.current?.validateFields();
      await dispatch(verifyPhoneOtp({ email, phone: `+${phone}`, code: phoneOtp })).unwrap();
      await signIn();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  async function resendCode(field) {
    const currentForm = isSignUpMode ? signUpFormRef.current : signInFormRef.current;
    try {
      if (field === 'emailOtp') {
        const { email } = await currentForm?.validateFields();
        await dispatch(resendEmailCode({ email })).unwrap();

        infoToastHandler('Code is send to you email');
      } else if (field === 'phoneOtp') {
        const { email, phone } = await currentForm?.validateFields();
        await dispatch(savePhone({ email, phone: `+${phone}` })).unwrap();

        infoToastHandler('Code is send to you phone');
      }
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            verticalLabelPadding: '0 0 0.5rem 0',
            itemMarginBottom: '1.5rem'
          }
        }
      }}>
      <div className="auth-wrapper align-items-center d-flex" style={sidebarBackground}>
        <div className="container">
          <Row className="no-gutters justify-content-center">
            <Col md="6" lg="5" className="bg-dark">
              <AuthAside
                title="Kraal,"
                text="Clarity, connection, and control for every herd."
                subtext={isSignUpMode ? 'Sign up to join Kraal.' : null}
              />
            </Col>

            <Col md="6" lg="5" className="bg-white p-5">
              <Title className="mb-4" level={3}>
                {isSignUpMode ? 'Sign Up' : 'Sign In'}
              </Title>

              {isSignUpMode ? (
                <SignUpForm
                  formRef={signUpFormRef}
                  loading={loading}
                  step={step}
                  handleNextStep={handleNextStep}
                  resendCode={resendCode}
                />
              ) : (
                <SignInForm
                  agreementFieldVisibility={agreementFieldVisibility}
                  formRef={signInFormRef}
                  loading={loading}
                  step={step}
                  signIn={signIn}
                  sendPhone={sendPhone}
                  resendCode={resendCode}
                  verifyEmail={verifyEmail}
                  verifyPhone={verifyPhone}
                />
              )}

              <AuthFooter isSignUpMode={isSignUpMode} switchToSignIn={switchToSignIn} />
            </Col>
          </Row>
        </div>
      </div>
    </ConfigProvider>
  );
}

export default Auth;
